<template>
  <main dir="rtl" class="flex-1 pb-8">
    <div class="mx-auto px-4 sm:px-6 lg:px-8">
      <div class="flex flex-wrap items-center justify-between gap-3 mb-6">
        <h2 class="text-2xl font-bold text-gray-900">بحث وحذف ربط الطالب بالفترة</h2>
        <button
          @click="reloadCurrentSchedule"
          :disabled="!selectedScheduleId || loading"
          class="bg-gray-100 hover:bg-gray-200 text-gray-700 px-4 py-2 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
        >
          تحديث
        </button>
      </div>

      <div v-if="errorMessage" class="mb-4 bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-red-700">
        {{ errorMessage }}
      </div>

      <!-- Search section: by schedule, then by student -->
      <div class="bg-white rounded-lg shadow p-4 mb-6">
        <h3 class="text-lg font-semibold text-gray-900 mb-3">البحث عن الروابط</h3>
        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          <div>
            <label class="block text-xs text-gray-500 mb-1">الجدول</label>
            <select
              v-model="selectedScheduleId"
              @change="onScheduleChanged"
              class="w-full p-2 border border-gray-300 rounded-md"
            >
              <option value="">اختر الجدول</option>
              <option v-for="schedule in schedules" :key="schedule.id" :value="schedule.id">
                {{ schedule.title }} ({{ schedule.is_paid ? 'مدفوع' : 'مجاني' }})
              </option>
            </select>
          </div>

          <div>
            <label class="block text-xs text-gray-500 mb-1">الطالب</label>
            <select
              v-model="selectedStudentId"
              @change="resetPaging"
              :disabled="!selectedScheduleId || !studentOptions.length"
              class="w-full p-2 border border-gray-300 rounded-md disabled:bg-gray-100"
            >
              <option value="">كل الطلاب في الجدول</option>
              <option v-for="student in studentOptions" :key="student.id" :value="student.id">
                {{ student.name }}
              </option>
            </select>
          </div>

          <div>
            <label class="block text-xs text-gray-500 mb-1">الحالة</label>
            <select v-model="selectedStatus" @change="resetPaging" class="w-full p-2 border border-gray-300 rounded-md">
              <option value="">كل الحالات</option>
              <option value="active">نشط</option>
              <option value="paused">موقوف</option>
              <option value="cancelled">ملغي</option>
              <option value="completed">مكتمل</option>
            </select>
          </div>
        </div>
        <p v-if="!selectedScheduleId" class="text-xs text-gray-500 mt-3">اختر الجدول أولاً لعرض الطلاب المرتبطين به.</p>
      </div>

      <div v-if="loading" class="text-center py-8 text-gray-600">جاري التحميل...</div>

      <!-- Results -->
      <div v-if="selectedScheduleId" class="bg-white rounded-lg shadow overflow-x-auto">
        <div class="px-4 py-3 border-b border-gray-100 flex flex-wrap items-center justify-between gap-3">
          <span class="text-sm text-gray-600">المحدد للحذف: {{ selectedEnrollmentIds.length }}</span>
          <div class="flex items-center gap-2">
            <select v-model.number="perPage" @change="resetPaging" class="p-2 border border-gray-300 rounded-md text-sm">
              <option :value="10">10 لكل صفحة</option>
              <option :value="20">20 لكل صفحة</option>
              <option :value="50">50 لكل صفحة</option>
            </select>
            <button
              class="px-3 py-1 rounded border border-gray-200 bg-white text-sm disabled:opacity-50 disabled:cursor-not-allowed"
              :disabled="currentPage <= 1"
              @click="currentPage--"
            >
              السابق
            </button>
            <span class="text-sm text-gray-600">
              صفحة {{ currentPage }} من {{ totalPages }} ({{ filteredEnrollments.length }} عنصر)
            </span>
            <button
              class="px-3 py-1 rounded border border-gray-200 bg-white text-sm disabled:opacity-50 disabled:cursor-not-allowed"
              :disabled="currentPage >= totalPages"
              @click="currentPage++"
            >
              التالي
            </button>
          </div>
          <button
            @click="deleteSelectedEnrollments"
            :disabled="!selectedEnrollmentIds.length || loading"
            class="text-red-700 bg-red-50 hover:bg-red-100 disabled:opacity-50 disabled:cursor-not-allowed px-3 py-1 rounded"
          >
            حذف المحدد
          </button>
        </div>
        <table class="min-w-full">
          <thead class="bg-gray-50">
            <tr>
              <th class="px-4 py-3 text-center text-xs font-medium text-gray-500">
                <input
                  type="checkbox"
                  :checked="isAllPageSelected"
                  @change="toggleSelectAllOnPage($event.target.checked)"
                  class="h-4 w-4"
                />
              </th>
              <th class="px-4 py-3 text-center text-xs font-medium text-gray-500">الطالب</th>
              <th class="px-4 py-3 text-center text-xs font-medium text-gray-500">الجدول</th>
              <th class="px-4 py-3 text-center text-xs font-medium text-gray-500">اليوم / الفترة</th>
              <th class="px-4 py-3 text-center text-xs font-medium text-gray-500">المعلم</th>
              <th class="px-4 py-3 text-center text-xs font-medium text-gray-500">المدة</th>
              <th class="px-4 py-3 text-center text-xs font-medium text-gray-500">الحالة</th>
              <th class="px-4 py-3 text-center text-xs font-medium text-gray-500">إجراءات</th>
            </tr>
          </thead>
          <tbody class="divide-y divide-gray-200">
            <tr v-for="row in pagedEnrollments" :key="row.id">
              <td class="px-4 py-3 text-sm text-center">
                <input
                  type="checkbox"
                  :checked="selectedEnrollmentIds.includes(row.id)"
                  @change="toggleEnrollmentSelection(row.id, $event.target.checked)"
                  class="h-4 w-4"
                />
              </td>
              <td class="px-4 py-3 text-sm text-center">{{ row.student_name }}</td>
              <td class="px-4 py-3 text-sm text-center">{{ row.schedule_title }}</td>
              <td class="px-4 py-3 text-sm text-center">
                {{ weekdayLabel(row.weekday) }} - {{ row.slot_name }} ({{ row.slot_order }})
              </td>
              <td class="px-4 py-3 text-sm text-center">{{ row.teacher_name }}</td>
              <td class="px-4 py-3 text-sm text-center">
                {{ row.enrollment_start_date }}<span v-if="row.enrollment_end_date"> - {{ row.enrollment_end_date }}</span>
              </td>
              <td class="px-4 py-3 text-sm text-center">{{ statusLabel(row.status) }}</td>
              <td class="px-4 py-3 text-sm text-center">
                <button
                  @click="deleteEnrollment(row)"
                  :disabled="loading"
                  class="text-red-700 bg-red-50 hover:bg-red-100 disabled:opacity-50 disabled:cursor-not-allowed px-3 py-1 rounded"
                >
                  حذف
                </button>
              </td>
            </tr>
            <tr v-if="!filteredEnrollments.length && !loading">
              <td colspan="8" class="px-4 py-6 text-center text-gray-500">لا توجد روابط مطابقة</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </main>
</template>

<script>
import { defineComponent } from 'vue';
import {
  deleteIqraStudentSlotEnrollment,
  listIqraSchedules,
  listIqraStudentSlotEnrollments,
} from '@/services/iqraSchedules';

// Generous cap so the student dropdown reflects every enrollment in the schedule.
const SCHEDULE_ENROLLMENTS_FETCH_SIZE = 500;

export default defineComponent({
  name: 'IqraStudentSlotEnrollmentSearch',
  data() {
    return {
      loading: false,
      errorMessage: '',
      schedules: [],
      allEnrollments: [],
      selectedScheduleId: '',
      selectedStudentId: '',
      selectedStatus: '',
      selectedEnrollmentIds: [],
      currentPage: 1,
      perPage: 20,
    };
  },
  computed: {
    studentOptions() {
      const unique = new Map();
      this.allEnrollments.forEach((row) => {
        if (row.student_id == null) return;
        const id = Number(row.student_id);
        if (!unique.has(id)) {
          unique.set(id, { id, name: row.student_name || `#${id}` });
        }
      });
      return Array.from(unique.values()).sort((a, b) => a.name.localeCompare(b.name, 'ar'));
    },
    filteredEnrollments() {
      return this.allEnrollments.filter((row) => {
        if (this.selectedStudentId && Number(row.student_id) !== Number(this.selectedStudentId)) return false;
        if (this.selectedStatus && row.status !== this.selectedStatus) return false;
        return true;
      });
    },
    totalPages() {
      return Math.max(1, Math.ceil(this.filteredEnrollments.length / this.perPage));
    },
    pagedEnrollments() {
      const start = (this.currentPage - 1) * this.perPage;
      return this.filteredEnrollments.slice(start, start + this.perPage);
    },
    isAllPageSelected() {
      if (!this.pagedEnrollments.length) return false;
      return this.pagedEnrollments.every((row) => this.selectedEnrollmentIds.includes(row.id));
    },
  },
  watch: {
    totalPages(newTotal) {
      if (this.currentPage > newTotal) {
        this.currentPage = newTotal;
      }
    },
  },
  async mounted() {
    await this.loadSchedules();
  },
  methods: {
    async loadSchedules() {
      this.loading = true;
      this.errorMessage = '';
      try {
        const result = await listIqraSchedules({ page: 1, size: 200, is_active: true });
        this.schedules = result?.data || [];
      } catch (error) {
        this.errorMessage = error?.message || 'فشل في تحميل جداول اقرأ';
      } finally {
        this.loading = false;
      }
    },
    async onScheduleChanged() {
      this.selectedStudentId = '';
      this.selectedStatus = '';
      this.resetPaging();
      await this.loadScheduleEnrollments();
    },
    async reloadCurrentSchedule() {
      await this.loadScheduleEnrollments();
    },
    async loadScheduleEnrollments() {
      this.selectedEnrollmentIds = [];
      if (!this.selectedScheduleId) {
        this.allEnrollments = [];
        return;
      }
      this.loading = true;
      this.errorMessage = '';
      try {
        const result = await listIqraStudentSlotEnrollments({
          schedule_id: Number(this.selectedScheduleId),
          page: 1,
          size: SCHEDULE_ENROLLMENTS_FETCH_SIZE,
        });
        this.allEnrollments = result?.data || [];
        const total = Number(result?.pagination?.total ?? this.allEnrollments.length);
        if (total > this.allEnrollments.length) {
          this.errorMessage = `يوجد ${total} ربط في هذا الجدول، تم عرض أول ${this.allEnrollments.length} فقط.`;
        }
      } catch (error) {
        this.errorMessage = error?.message || 'فشل في تحميل روابط الجدول';
        this.allEnrollments = [];
      } finally {
        this.loading = false;
      }
    },
    resetPaging() {
      this.currentPage = 1;
      this.selectedEnrollmentIds = [];
    },
    async deleteEnrollment(row) {
      const confirmed = window.confirm('هل أنت متأكد من حذف ربط الطالب بهذه الفترة؟');
      if (!confirmed) return;

      this.loading = true;
      this.errorMessage = '';
      try {
        await deleteIqraStudentSlotEnrollment(row.id);
        await this.loadScheduleEnrollments();
      } catch (error) {
        this.errorMessage = error?.message || 'فشل في حذف الربط';
      } finally {
        this.loading = false;
      }
    },
    async deleteSelectedEnrollments() {
      if (!this.selectedEnrollmentIds.length) return;

      const confirmed = window.confirm(`هل أنت متأكد من حذف ${this.selectedEnrollmentIds.length} ربط محدد؟`);
      if (!confirmed) return;

      this.loading = true;
      this.errorMessage = '';
      try {
        const requests = this.selectedEnrollmentIds.map((enrollmentId) => deleteIqraStudentSlotEnrollment(Number(enrollmentId)));
        const result = await Promise.allSettled(requests);
        const successCount = result.filter((item) => item.status === 'fulfilled').length;
        const failedCount = result.length - successCount;
        await this.loadScheduleEnrollments();
        alert(`تم حذف المحدد. نجاح: ${successCount}، فشل: ${failedCount}`);
      } catch (error) {
        this.errorMessage = error?.message || 'فشل في حذف الروابط المحددة';
      } finally {
        this.loading = false;
      }
    },
    toggleEnrollmentSelection(enrollmentId, isChecked) {
      const id = Number(enrollmentId);
      if (isChecked) {
        if (!this.selectedEnrollmentIds.includes(id)) {
          this.selectedEnrollmentIds.push(id);
        }
        return;
      }
      this.selectedEnrollmentIds = this.selectedEnrollmentIds.filter((item) => Number(item) !== id);
    },
    toggleSelectAllOnPage(isChecked) {
      const pageIds = this.pagedEnrollments.map((row) => Number(row.id));
      if (!isChecked) {
        this.selectedEnrollmentIds = this.selectedEnrollmentIds.filter((id) => !pageIds.includes(Number(id)));
        return;
      }
      const merged = new Set(this.selectedEnrollmentIds.map((id) => Number(id)));
      pageIds.forEach((id) => merged.add(id));
      this.selectedEnrollmentIds = Array.from(merged);
    },
    weekdayLabel(value) {
      const map = {
        1: 'الإثنين',
        2: 'الثلاثاء',
        3: 'الأربعاء',
        4: 'الخميس',
        5: 'الجمعة',
        6: 'السبت',
        7: 'الأحد',
      };
      return map[value] || `اليوم ${value}`;
    },
    statusLabel(value) {
      const map = {
        active: 'نشط',
        paused: 'موقوف',
        cancelled: 'ملغي',
        completed: 'مكتمل',
      };
      return map[value] || value;
    },
  },
});
</script>
